import { useEffect, useState, type FormEvent } from "react";
import { api, type User } from "../api";
import { Credit } from "../Credit";
import { initials } from "../format";
import { Banner, PageHeader } from "../ui";

export function AccountPage({ user, onLogout }: { user: User; onLogout: () => Promise<void> }) {
  const [users, setUsers] = useState<User[]>([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  async function load() {
    const result = await api<{ users: User[] }>("/api/auth/users");
    setUsers(result.users);
  }

  useEffect(() => {
    load().catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "Chargement impossible"));
  }, []);

  async function create(event: FormEvent) {
    event.preventDefault();
    setError("");
    setNotice("");
    try {
      await api("/api/auth/users", { method: "POST", body: JSON.stringify({ name, email, password }) });
      setName("");
      setEmail("");
      setPassword("");
      setNotice(`${name} peut maintenant se connecter.`);
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Création impossible");
    }
  }

  async function remove(target: User) {
    if (!window.confirm(`Supprimer le compte de ${target.name} ?`)) return;
    setError("");
    try {
      await api(`/api/auth/users/${target.id}`, { method: "DELETE" });
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Suppression impossible");
    }
  }

  return (
    <section>
      <PageHeader
        kicker="Équipe"
        title="Compte"
        lede="Les personnes qui peuvent gérer les télés, les médias et les playlists."
        action={<button type="button" onClick={() => void onLogout()}>Se déconnecter</button>}
      />
      <article className="card profile-card">
        <span className="avatar lg" aria-hidden="true">{initials(user.name)}</span>
        <div>
          <h2>{user.name}</h2>
          <p className="muted">{user.email}</p>
          <Credit />
        </div>
      </article>
      {error ? <Banner tone="error">{error}</Banner> : null}
      {notice ? <Banner tone="ok">{notice}</Banner> : null}
      <div className="detail-grid block">
        <form className="card stack" onSubmit={(event) => void create(event)}>
          <h2>Ajouter un accès</h2>
          <p className="muted">Le mot de passe fait au moins 10 caractères. La personne se connecte ensuite avec son e-mail.</p>
          <label>
            Nom
            <input value={name} onChange={(event) => setName(event.target.value)} required minLength={2} autoComplete="name" />
          </label>
          <label>
            E-mail
            <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required autoComplete="off" />
          </label>
          <label>
            Mot de passe
            <span className="field-hint">10 caractères minimum.</span>
            <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} required minLength={10} autoComplete="new-password" />
          </label>
          <button className="primary" type="submit">Créer le compte</button>
        </form>
        <section className="card">
          <h2>Accès actuels</h2>
          <ul className="user-list">
            {users.map((entry) => (
              <li key={entry.id}>
                <span className="avatar" aria-hidden="true">{initials(entry.name)}</span>
                <div>
                  <strong>{entry.name}</strong>
                  <p className="muted">{entry.email}</p>
                </div>
                {entry.id === user.id ? <span className="pill online"><span className="dot" />Vous</span> : <button type="button" className="danger" onClick={() => void remove(entry)}>Supprimer</button>}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </section>
  );
}
